import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://pis.ap.io.vn');

// Create axios instance with auth token
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/scanner`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

// Scanner API
export const scannerAPI = {
  // Devices
  async getDevices(params?: { active_only?: boolean }) {
    const response = await apiClient.get('/devices/', { params });
    return response.data;
  },

  async getDevice(id: number) {
    const response = await apiClient.get(`/devices/${id}/`);
    return response.data;
  },

  async createDevice(data: any) {
    const response = await apiClient.post('/devices/', data);
    return response.data;
  },

  async updateDevice(id: number, data: any) {
    const response = await apiClient.patch(`/devices/${id}/`, data);
    return response.data;
  },

  async testConnection(id: number) {
    const response = await apiClient.post(`/devices/${id}/test_connection/`);
    return response.data;
  },

  async getDeviceStatus(id: number) {
    const response = await apiClient.get(`/devices/${id}/get_status/`);
    return response.data;
  },

  async getScanModes(id: number) {
    const response = await apiClient.get(`/devices/${id}/scan_modes/`);
    return response.data;
  },

  // Jobs
  async getJobs(params?: {
    status?: string;
    device?: number;
    date_from?: string;
    limit?: number;
  }) {
    const response = await apiClient.get('/jobs/', { params });
    return response.data;
  },

  async getJobDetail(id: number) {
    const response = await apiClient.get(`/jobs/${id}/`);
    return response.data;
  },

  async createJob(data: {
    device_id: number;
    patient_id?: string;
    patient_name?: string;
    scan_mode?: string;
    case_id?: number;
    scan_params?: any;
  }) {
    const response = await apiClient.post('/jobs/', data);
    return response.data;
  },

  async stopJob(id: number) {
    const response = await apiClient.post(`/jobs/${id}/stop/`);
    return response.data;
  },

  async getJobProgress(id: number) {
    const response = await apiClient.get(`/jobs/${id}/progress/`);
    return response.data;
  },

  async uploadCallback(data: {
    job_id: string;
    filename: string;
    object_name: string;
    file_size: number;
    checksum?: string;
    timestamp: number;
  }) {
    const response = await apiClient.post('/jobs/upload_callback/', data);
    return response.data;
  },

  // Files
  async getFiles(params?: { job?: number; uploaded?: boolean }) {
    const response = await apiClient.get('/files/', { params });
    return response.data;
  },

  async getFile(id: number) {
    const response = await apiClient.get(`/files/${id}/`);
    return response.data;
  },
};

// WebSocket for real-time updates
export class ScannerWebSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;

  constructor(jobId: string, onMessage: (data: any) => void) {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = import.meta.env.VITE_WS_URL || window.location.host;
    this.url = `${wsProtocol}//${wsHost}/ws/scanner/${jobId}/`;

    this.connect(onMessage);
  }

  private connect(onMessage: (data: any) => void) {
    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          onMessage(data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.attemptReconnect(onMessage);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }

  private attemptReconnect(onMessage: (data: any) => void) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      setTimeout(() => this.connect(onMessage), this.reconnectDelay);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  public disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  public send(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket is not connected');
    }
  }
}

export default scannerAPI;
